import { useEffect, useMemo } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { Form } from "../../../components/Form";
import { MoneyInput } from "../../../components/MoneyInput";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type {
  OfferFixedPrice,
  OfferFixedPriceEntry,
} from "@KassaroAB/shared/types/offer";
import type {
  ProductId,
  ProductVariantId,
} from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import type { Currency } from "@KassaroAB/shared/types/enums/currency";
import {
  asDecimal,
  createMoney,
} from "@KassaroAB/shared/utils/money";
import { OfferCommonFields } from "./OfferCommonFields";
import { OfferTargetFields } from "./OfferTargetFields";
import type { OfferTypeFormProps } from "./offerFormTypes";
import { Fields, PriceRow, PriceTitle } from "./offerFormStyles";
import {
  buildOfferBase,
  emptyOfferTarget,
  getOfferCommonDefaults,
} from "./offerUtils";
import { useOfferFormCatalog } from "./useOfferFormCatalog";
import { useOfferFormLifecycle } from "./useOfferFormLifecycle";

const moneyFromMajor = (major: number, currency: Currency): Money | null => {
  const minor = Math.round(Number(major) * 100);
  return minor > 0 ? createMoney(minor, currency) : null;
};

export const FixedPriceForm = ({
  formId,
  offer,
  savedTick,
  onSubmit,
  onDirtyChange,
  onTypeChange,
}: OfferTypeFormProps) => {
  const { t } = useTranslation();
  const { currency, products, productOptions, groupOptions } =
    useOfferFormCatalog();
  const fixedOffer =
    offer?.type === OfferType.FIXED_PRICE ? (offer as OfferFixedPrice) : null;
  const isEditing = !!offer;
  const initialPrices: OfferFixedPriceEntry[] = fixedOffer?.prices ?? [];

  const form = useForm({
    defaultValues: {
      ...getOfferCommonDefaults(
        fixedOffer ??
          (offer ? { ...offer, type: OfferType.FIXED_PRICE } : undefined),
      ),
      type: OfferType.FIXED_PRICE as OfferType,
      target:
        fixedOffer?.target ??
        (offer && "target" in offer ? offer.target : emptyOfferTarget()),
      unitPriceMajor: fixedOffer?.unitPrice
        ? asDecimal(fixedOffer.unitPrice)
        : 0,
      takeawayPriceMajor: fixedOffer?.takeawayPrice
        ? asDecimal(fixedOffer.takeawayPrice)
        : 0,
      prices: initialPrices,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...buildOfferBase(value),
        type: OfferType.FIXED_PRICE,
        target: value.target,
        unitPrice: moneyFromMajor(value.unitPriceMajor, currency),
        takeawayPrice: moneyFromMajor(value.takeawayPriceMajor, currency),
        prices: value.prices,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  const target = useStore(form.store, (state) => state.values.target);
  const prices = useStore(form.store, (state) => state.values.prices);
  const unitPriceMajor = useStore(
    form.store,
    (state) => state.values.unitPriceMajor,
  );
  const takeawayPriceMajor = useStore(
    form.store,
    (state) => state.values.takeawayPriceMajor,
  );
  useOfferFormLifecycle({ form, savedTick, onDirtyChange, isDirty });

  const listedProducts = useMemo(() => {
    const groupProductIds = new Set(
      products
        .filter(
          (product) =>
            product.groupId != null && target.groupIds.includes(product.groupId),
        )
        .map((product) => product.id),
    );
    const ids = new Set([...target.productIds, ...groupProductIds]);
    return productOptions.filter((product) => ids.has(product.id));
  }, [productOptions, products, target.groupIds, target.productIds]);

  useEffect(() => {
    const allowed = new Set(listedProducts.map((product) => product.id));
    form.setFieldValue("prices", (current) => {
      const next = current.filter((entry) => allowed.has(entry.productId));
      return next.length === current.length ? current : next;
    });
  }, [form, listedProducts]);

  const upsertPrice = (
    productId: ProductId,
    variantId: ProductVariantId | null,
    field: "unitPrice" | "takeawayPrice",
    major: number,
  ) => {
    form.setFieldValue("prices", (current) => {
      const rest = current.filter(
        (entry) =>
          entry.productId !== productId ||
          (entry.variantId ?? null) !== (variantId ?? null),
      );
      const existing = current.find(
        (entry) =>
          entry.productId === productId &&
          (entry.variantId ?? null) === (variantId ?? null),
      );
      const nextUnit =
        field === "unitPrice"
          ? moneyFromMajor(major, currency)
          : (existing?.unitPrice ?? null);
      const nextTakeaway =
        field === "takeawayPrice"
          ? moneyFromMajor(major, currency)
          : (existing?.takeawayPrice ?? null);
      if (nextUnit == null && nextTakeaway == null) {
        return rest;
      }
      return [
        ...rest,
        {
          productId,
          variantId,
          unitPrice: nextUnit,
          takeawayPrice: nextTakeaway,
        },
      ];
    });
  };

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <OfferCommonFields
        form={form}
        isEditing={isEditing}
        onTypeChange={onTypeChange}
      />
      <FormSection title={t("offerForm.additionalSettings")}>
        <Fields>
          <form.Field name="target">
            {(field) => (
              <OfferTargetFields
                value={field.state.value}
                onChange={field.handleChange}
                products={productOptions}
                groups={groupOptions}
                productsLabel={t("offerForm.products")}
                groupsLabel={t("offerForm.productGroups")}
              />
            )}
          </form.Field>
          <form.Field name="unitPriceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.unitPrice")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="takeawayPriceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.takeawayPrice")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>

      {listedProducts.length > 0 ? (
        <FormSection title={t("offerForm.prices")}>
          <Fields>
            {listedProducts.map((product) => {
              const variants = product.variants;
              if (variants.length > 0) {
                return (
                  <div key={product.id}>
                    <PriceTitle>{product.name}</PriceTitle>
                    {variants.map((variant) => {
                      const entry = prices.find(
                        (item) =>
                          item.productId === product.id &&
                          item.variantId === variant.id,
                      );
                      return (
                        <PriceRow key={variant.id}>
                          <PriceTitle>
                            {variant.name || variant.id}
                          </PriceTitle>
                          <MoneyInput
                            label={t("offerForm.unitPrice")}
                            amount={
                              entry?.unitPrice
                                ? asDecimal(entry.unitPrice)
                                : unitPriceMajor
                            }
                            currency={currency}
                            onAmountChange={(major) =>
                              upsertPrice(
                                product.id,
                                variant.id,
                                "unitPrice",
                                major,
                              )
                            }
                          />
                          <MoneyInput
                            label={t("offerForm.takeawayPrice")}
                            amount={
                              entry?.takeawayPrice
                                ? asDecimal(entry.takeawayPrice)
                                : takeawayPriceMajor
                            }
                            currency={currency}
                            onAmountChange={(major) =>
                              upsertPrice(
                                product.id,
                                variant.id,
                                "takeawayPrice",
                                major,
                              )
                            }
                          />
                        </PriceRow>
                      );
                    })}
                  </div>
                );
              }
              const entry = prices.find(
                (item) =>
                  item.productId === product.id && item.variantId == null,
              );
              return (
                <PriceRow key={product.id}>
                  <PriceTitle>{product.name}</PriceTitle>
                  <MoneyInput
                    label={t("offerForm.unitPrice")}
                    amount={
                      entry?.unitPrice
                        ? asDecimal(entry.unitPrice)
                        : unitPriceMajor
                    }
                    currency={currency}
                    onAmountChange={(major) =>
                      upsertPrice(product.id, null, "unitPrice", major)
                    }
                  />
                  <MoneyInput
                    label={t("offerForm.takeawayPrice")}
                    amount={
                      entry?.takeawayPrice
                        ? asDecimal(entry.takeawayPrice)
                        : takeawayPriceMajor
                    }
                    currency={currency}
                    onAmountChange={(major) =>
                      upsertPrice(product.id, null, "takeawayPrice", major)
                    }
                  />
                </PriceRow>
              );
            })}
          </Fields>
        </FormSection>
      ) : null}
    </Form>
  );
};
